import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { colocaToast } from "@/shared/utils/toast";

import { resolvePocketName } from "../constants/pockets.constants";
import { PocketsService } from "../services/pocketsService";
import type { Pocket } from "../types/pockets-api.types";

type DeletePocketModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pocket: Pocket | null;
  onDeleted?: () => void;
};

export const DeletePocketModal = ({ open, onOpenChange, pocket, onDeleted }: DeletePocketModalProps) => {
  const { t } = useTranslations("cards");
  const [deleting, setDeleting] = useState(false);

  const pocketName = pocket ? resolvePocketName(pocket) : "";

  const handleDelete = async () => {
    if (!pocket) return;
    setDeleting(true);
    try {
      await PocketsService.deletePocket(pocket.id);
      colocaToast.success(t("pocket_delete_success"));
      onOpenChange(false);
      onDeleted?.();
    } catch (err: unknown) {
      colocaToast.error(HttpApiService.getErrorMessage(err) || t("pocket_delete_error"));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("pocket_delete_modal_title")}
      description={t("pocket_delete_modal_description", { name: pocketName })}
      size="sm"
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <ColocaButton variant="ghost" onClick={() => onOpenChange(false)} disabled={deleting}>
            {t("create_identity_modal_button_cancel")}
          </ColocaButton>
          <ColocaButton variant="destructive" onClick={handleDelete} loading={deleting} disabled={deleting}>
            {t("pocket_delete_confirm")}
          </ColocaButton>
        </div>
      }
    >
      <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t("pocket_delete_warning")}</span>
      </div>
    </ColocaDialog>
  );
};
